import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CharacterInput,
  Column,
  ErrorMessage,
  Font,
  PrimaryButton,
  Row,
  SecondaryButton,
} from './DesignSystem';
import { Palette } from './Palette';
import { useAuthentication } from '@/services/AuthService';
import { useWindows } from './Windows';

export const RequireInviteCode = true;
const EnableInviteForm = true;

const InviteServerErrorCopy =
  'Unable to validate invite code right now. Please try again.';
const InviteNetworkErrorCopy =
  'Unable to connect. Please check your connection and try again.';

function inviteErrorCopy(error: unknown): string {
  if (error instanceof Error) {
    const normalized = error.message.trim().toLowerCase();
    if (
      normalized.includes('failed to fetch') ||
      normalized.includes('network') ||
      normalized.includes('connection')
    ) {
      return InviteNetworkErrorCopy;
    }
  }

  return InviteServerErrorCopy;
}

export function InviteForm({
  onGoToWaitlist,
  onGoToLogin,
}: {
  onGoToWaitlist: () => void;
  onGoToLogin: () => void;
}) {
  const { service: authService, state: authState } = useAuthentication();
  const { dispatch: windowDispatch } = useWindows();
  const [focusedIndex, setFocusedIndex] = useState<number | null>(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [code, _setCode] = useState<string>('');
  const setCode = useCallback(
    (code: string) => {
      _setCode(code.toUpperCase());
    },
    [_setCode],
  );
  const queryString = window.location.search;

  function extractCode(queryString: string): string | undefined {
    const queryComponents = queryString.split('code=');
    const codePortion = queryComponents[1];
    if (codePortion === undefined) {
      return undefined;
    }

    const candidateCode = codePortion.split('&')[0];
    if (candidateCode === undefined || candidateCode.length < 4) {
      return undefined;
    }

    return candidateCode;
  }
  const inviteCode = extractCode(queryString);

  useEffect(() => {
    if (inviteCode === undefined) {
      return;
    }

    setCode(inviteCode);
  }, [inviteCode, setCode]);

  useEffect(() => {
    if (focusedIndex === null) {
      return;
    }
    inputRefs.current[focusedIndex]?.focus();
  }, [focusedIndex]);

  const updateCharacter = useCallback(
    function updateCharacter({ index, code }: { index: number; code: string }) {
      return function (text: string) {
        if (index < 0) {
          return;
        }
        const character = text.at(0) ?? '';
        const newCode =
          code.slice(0, index) + character + code.slice(index + 1);
        setCode(newCode);
        setErrorMessage(null);
        if (character.length > 0) {
          setFocusedIndex(index + 1);
        } else if (index > 0) {
          setFocusedIndex(index - 1);
        } else {
          setFocusedIndex(0);
        }
      };
    },
    [setCode],
  );

  const continueBeyondInviteForm = useCallback(() => {
    windowDispatch({ type: 'show', id: 'auth' });
    windowDispatch({ type: 'hide', id: 'invite-code' });
    windowDispatch({ type: 'hide', id: 'header' });
  }, [windowDispatch]);

  useEffect(() => {
    if (authState.hasAuthenticated) {
      continueBeyondInviteForm();
    }
  }, [authState.hasAuthenticated, continueBeyondInviteForm]);

  const sendInviteCode = useCallback(
    function sendInviteCode({ code }: { code: string }) {
      (async () => {
        try {
          setIsLoading(true);
          setErrorMessage(null);
          const {} = await authService.sendRequest({
            endpoint: 'invitation',
            method: 'POST',
            bodyObj: { invite_code: code },
          });

          continueBeyondInviteForm();
          setIsLoading(false);
        } catch (error) {
          console.error('Failed to submit invite code', error);
          setErrorMessage(inviteErrorCopy(error));
          setIsLoading(false);
        }
      })();
    },
    [authService, continueBeyondInviteForm],
  );

  if (!EnableInviteForm || authState.hasAuthenticated) {
    return <></>;
  }

  const shouldExtend = code.slice(0, 4) === 'MELI';
  const completeCode = shouldExtend ? code.length === 8 : code.length >= 4;

  function Character({
    isLoading,
    index,
  }: {
    isLoading: boolean;
    index: number;
  }) {
    return (
      <CharacterInput
        inputRef={(el) => {
          inputRefs.current[index] = el;
        }}
        ariaLabel={`Invite code character ${index + 1}`}
        isLoading={isLoading}
        onChange={updateCharacter({ index, code })}
        value={code[index] ?? ''}
      />
    );
  }

  return (
    <Palette title="Invite" windowID="invite-code" headless={true}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (isLoading || !completeCode) {
            return;
          }

          sendInviteCode({ code });
        }}
      >
        <Column style={{ alignItems: 'center', marginBlockStart: 16 }}>
          <Row style={{ marginBlockEnd: 8 }}>
            {Array.from(Array(shouldExtend ? 8 : 4).keys()).map((index) => (
              <div key={index}>
                <Character index={index} isLoading={isLoading} />
              </div>
            ))}
          </Row>

          <PrimaryButton
            text={'Submit'}
            style={{
              paddingInline: 16,
              height: '48px',
              width: '100%',
              marginBlockStart: 8,
            }}
            isDisabled={!completeCode || isLoading}
            onClick={() => {}}
          />
          {errorMessage && (
            <div
              role="alert"
              style={{
                marginBlockStart: 8,
                textAlign: 'center',
              }}
            >
              <ErrorMessage message={errorMessage} />
            </div>
          )}
          <Row style={{ alignItems: 'center', height: '100%', width: '100%' }}>
            <Column
              style={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                gap: 8,
                marginBlockStart: 20,
              }}
            >
              <div>
                <Font kind="font-caption" style={{ color: '#000000d0' }}>
                  Join Waitlist
                </Font>
              </div>
              <SecondaryButton
                text="Join Waitlist"
                style={{ width: '100%' }}
                isDisabled={isLoading}
                onClick={() => {
                  onGoToWaitlist();
                  continueBeyondInviteForm();
                }}
              />
            </Column>
            <Column
              style={{
                width: '100%',
                alignItems: 'center',
                gap: 8,
                marginBlockStart: 20,
              }}
            >
              <div>
                <Font kind="font-caption" style={{ color: '#000000d0' }}>
                  Already have an account?
                </Font>
              </div>
              <SecondaryButton
                text="Go to Login"
                style={{ width: '100%' }}
                isDisabled={isLoading}
                onClick={() => {
                  onGoToLogin();
                  continueBeyondInviteForm();
                }}
              />
            </Column>
          </Row>
        </Column>
      </form>
    </Palette>
  );
}
